<template>
    <Page class="compliance-evidence-detail-page">
        <template #title>
            <div class="header-section">
                <Button
                    icon="pi pi-arrow-left"
                    text
                    severity="secondary"
                    @click="goBack"
                    class="back-button"
                />
                <div class="title-content">
                    <div class="breadcrumb">
                        <span class="breadcrumb-item">Live Mode</span>
                        <i class="pi pi-angle-right"></i>
                        <span class="breadcrumb-item">Process Dashboard</span>
                        <i class="pi pi-angle-right"></i>
                        <span
                            class="breadcrumb-item breadcrumb-link"
                            @click="goToEvidenceList"
                        >
                            Compliance Evidence
                        </span>
                        <i class="pi pi-angle-right"></i>
                        <span class="breadcrumb-item-current">Details</span>
                    </div>
                    <h1 class="page-title">
                        <i class="pi pi-shield"></i>
                        Evidence Details
                    </h1>
                </div>
            </div>
        </template>

        <Section v-if="loading">
            <div class="loading-state">
                <i class="pi pi-spinner pi-spin"></i>
                <p>Loading evidence...</p>
            </div>
        </Section>

        <Section v-else-if="evidence">
            <template #title>Evidence Information</template>

            <div class="detail-grid">
                <div class="detail-row">
                    <div class="detail-label">Type</div>
                    <div class="detail-value">
                        <Tag
                            :value="evidence.type === 'uri' ? 'Link' : 'Text'"
                            :severity="
                                evidence.type === 'uri' ? 'info' : 'secondary'
                            "
                        />
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Description</div>
                    <div class="detail-value description">
                        {{ evidence.description }}
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Evidence</div>
                    <div class="detail-value">
                        <a
                            v-if="evidence.type === 'uri'"
                            :href="evidence.value"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="evidence-link"
                        >
                            {{ evidence.value }}
                            <i class="pi pi-external-link" />
                        </a>
                        <div v-else class="evidence-text">
                            {{ evidence.value }}
                        </div>
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Task/Event</div>
                    <div class="detail-value">
                        <div class="task-info">
                            <i
                                :class="getEventIcon(evidence)"
                                :title="getEventTooltip(evidence)"
                                class="event-icon"
                            ></i>
                            <span
                                v-if="evidence.process_element_name"
                                class="task-name"
                            >
                                {{ evidence.process_element_name }}
                            </span>
                            <span
                                v-else-if="evidence.process_element_id"
                                class="element-id"
                                :title="evidence.process_element_id"
                            >
                                Task (unknown name)
                            </span>
                            <span v-else class="event-type">
                                {{ evidence.event_type }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Timestamp</div>
                    <div class="detail-value">
                        {{ formatDateTime(evidence.timestamp) }}
                    </div>
                </div>

                <div class="detail-row" v-if="evidence.created_by">
                    <div class="detail-label">Created By</div>
                    <div class="detail-value">
                        {{ evidence.created_by }}
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Event ID</div>
                    <div class="detail-value monospace">
                        {{ evidence.event_id }}
                    </div>
                </div>

                <div class="detail-row">
                    <div class="detail-label">Process Instance ID</div>
                    <div class="detail-value monospace">
                        {{ evidence.process_instance_id }}
                    </div>
                </div>

                <div class="detail-row" v-if="evidence.business_key">
                    <div class="detail-label">Business Key</div>
                    <div class="detail-value monospace">
                        {{ evidence.business_key }}
                    </div>
                </div>
            </div>
        </Section>

        <Section v-else>
            <div class="empty-state">
                <i class="pi pi-info-circle"></i>
                <p>Evidence not found</p>
            </div>
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import Tag from "primevue/tag";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import { useStekStore } from "@/composables/stek-manager";
import { type types, useClient } from "@/lib/openapi";
import { startStekLiveMgtGetComplianceEvidenceStartPost } from "@/lib/openapi/gen/sdk.gen";

type ComplianceEvidenceView = types.ComplianceEvidenceView;

defineOptions({
    name: "ComplianceEvidenceDetailPage",
});

const route = useRoute();
const router = useRouter();

const stekStore = useStekStore();
const { stek } = storeToRefs(stekStore);

const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const evidence = ref<ComplianceEvidenceView | null>(null);
const loading = ref(false);

const fetchEvidence = async () => {
    const processInstanceId = route.query.process_instance_id as string;
    const evidenceId = route.query.evidence_id as string;

    if (!processInstanceId || !evidenceId) {
        return;
    }

    loading.value = true;
    try {
        const { data } =
            await startStekLiveMgtGetComplianceEvidenceStartPost<true>({
                client,
                throwOnError: true,
                body: {
                    process_instance_id: processInstanceId,
                    hours: 720, // 30 days
                },
            });

        // Find the specific evidence by ID
        evidence.value = data.find((e) => e.id === evidenceId) || null;
    } catch (error) {
        console.error("Failed to fetch compliance evidence:", error);
        evidence.value = null;
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    const evidenceId = route.query.evidence_id as string;

    // Try to get from sessionStorage first (fast path)
    if (evidenceId) {
        const cached = sessionStorage.getItem(`evidence_${evidenceId}`);
        if (cached) {
            try {
                evidence.value = JSON.parse(cached);
                // Clear from sessionStorage after use
                sessionStorage.removeItem(`evidence_${evidenceId}`);
                return;
            } catch {
                // Invalid cached data, fall through to fetch
            }
        }
    }

    // Fallback: fetch from API
    if (stek.value) {
        await fetchEvidence();
    }
});

// Watch for stek to become available
watch(
    () => stek.value,
    (newStek) => {
        if (newStek && !evidence.value) {
            fetchEvidence();
        }
    }
);

const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
};

const getEventIcon = (evidence: ComplianceEvidenceView): string => {
    const eventType = evidence.event_type?.toLowerCase() || "";

    if (
        eventType.includes("task_completed") ||
        eventType.includes("task_finished")
    ) {
        return "pi pi-check-circle";
    }

    if (
        eventType.includes("process_completed") ||
        eventType.includes("process_finished")
    ) {
        return "pi pi-flag-fill";
    }

    return "";
};

const getEventTooltip = (evidence: ComplianceEvidenceView): string => {
    const eventType = evidence.event_type?.toLowerCase() || "";

    if (eventType.includes("task_completed")) {
        return "Task completed";
    }
    if (eventType.includes("process_completed")) {
        return "Process completed";
    }

    return "";
};

const goBack = () => {
    router.back();
};

const goToEvidenceList = () => {
    router.push({
        name: "complianceEvidencePage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
        },
        query: {
            process_instance_id: route.query.process_instance_id,
            business_key: route.query.business_key,
        },
    });
};
</script>

<style scoped lang="scss">
.compliance-evidence-detail-page {
    max-width: 1200px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    .back-button {
        margin-top: 0.5rem;
    }

    .title-content {
        flex: 1;

        .breadcrumb {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
            color: var(--text-color-secondary);
            margin-bottom: 0.5rem;

            .breadcrumb-item {
                color: var(--text-color-secondary);
            }

            .breadcrumb-link {
                color: var(--stekz-primary-color);
                cursor: pointer;

                &:hover {
                    text-decoration: underline;
                }
            }

            .breadcrumb-item-current {
                color: var(--text-color);
                font-weight: 500;
            }

            i {
                font-size: 0.75rem;
            }
        }

        .page-title {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin: 0;

            i {
                color: var(--primary-color);
                font-size: 1.75rem;
                flex-shrink: 0;
            }
        }
    }
}

.detail-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.detail-row {
    display: grid;
    grid-template-columns: 200px 1fr;
    gap: 1rem;
    align-items: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }
}

.detail-label {
    font-weight: 600;
    color: var(--text-color);
    font-size: 0.875rem;
    padding-top: 0.25rem;
}

.detail-value {
    font-size: 0.875rem;
    color: var(--text-color);
    word-wrap: break-word;
    line-height: 1.6;

    &.description {
        white-space: pre-wrap;
        line-height: 1.8;
    }

    &.monospace {
        font-family: monospace;
        font-size: 0.813rem;
        background: var(--surface-100);
        padding: 0.5rem;
        border-radius: var(--border-radius);
    }
}

.evidence-link {
    color: var(--stekz-primary-color);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    word-break: break-all;

    i {
        font-size: 0.813rem;
        flex-shrink: 0;
    }

    &:hover {
        text-decoration: underline;
    }
}

.evidence-text {
    white-space: pre-wrap;
    line-height: 1.6;
}

.task-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.event-icon {
    font-size: 1.25rem;

    &.pi-check-circle {
        color: var(--green-500);
    }

    &.pi-flag-fill {
        color: var(--blue-500);
    }
}

.task-name {
    font-weight: 500;
}

.element-id {
    color: var(--text-color-secondary);
    font-style: italic;
    cursor: help;
}

.event-type {
    color: var(--text-color-secondary);
    font-style: italic;
}

.loading-state {
    text-align: center;
    padding: 3rem;
    color: var(--text-color-secondary);

    i {
        font-size: 2rem;
        margin-bottom: 1rem;
    }

    p {
        margin: 0;
        font-size: 1rem;
    }
}

.empty-state {
    text-align: center;
    padding: 3rem;
    color: var(--text-color-secondary);

    i {
        font-size: 3rem;
        margin-bottom: 1rem;
        opacity: 0.5;
    }

    p {
        margin: 0;
        font-size: 1rem;
    }
}
</style>
